<template>
  <BaseModalLarge
    :heading="$t('admin.treatwell.photos_modal.heading')"
    :loading="loadingVenue"
    useForm
    small
    testId="task-provide-company-photos"
    @submit="submit"
    @close="close"
  >
    <Flex direction="column">
      <BaseText>{{ $t('treatwell.images.description') }}</BaseText>
      <BaseText
        v-if="!hasActiveVenue"
        v-test="'stock-image-disclaimer'"
      >
        {{ $t('admin.treatwell.photos_modal.stock_image_disclaimer') }}
      </BaseText>
      <BaseAlert
        color="warning"
        :text="$t('admin.treatwell.photos_modal.alert')"
        :primaryAction="{
          text: $t('admin.treatwell.photos_modal.primary_action'),
          href: photoGuidelinesUrl,
        }"
      />
      <BaseText
        v-if="imageRejections.includes('orientation')"
        color="error"
        v-test="'orientationError'"
      >
        {{ $t('global.image_must_be_landscape') }}
      </BaseText>
      <BaseText
        v-if="imageRejections.includes('resolution')"
        color="error"
        v-test="'resolutionError'"
      >
        {{ $t('global.image_resolution_too_low', { width: minWidth, height: minHeight }) }}
      </BaseText>
      <BaseText
        v-if="fileAmountWarning"
        color="error"
        v-test="'file-amount-warning'"
      >
        {{ fileAmountWarning }}
      </BaseText>
      <BaseText
        v-else-if="images.length >= maxPhotos"
        v-test="'max-photos-reached'"
      >
        {{ $t('admin.treatwell.photos_modal.max_photos_required') }}
      </BaseText>
      <BaseText
        v-if="saveFailed"
        color="error"
        v-test="'company-photos-save-error'"
      >
        {{ $t('global.image_upload_fail') }}
      </BaseText>
      <BaseGallery
        v-model="galleryImages"
        :maxLength="maxPhotos"
        cloudinaryPreset="tw-venue-images"
        :imaginaryCategory="imaginaryCategory"
        enforceLandscape
        :minWidth="minWidth"
        :minHeight="minHeight"
        @image-rejected="addImageRejection"
        @upload-started="imageRejections = []"
      />
    </Flex>
    <template #footer>
      <BaseButton
        color="inverted"
        @click="close"
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        :loading="isSaving"
        :disabled="loadingVenue || !images.length"
        submitForm
        v-test="'company-photos-submit'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModalLarge>
</template>

<script
  lang="ts"
  setup
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useGalleryImages } from '@/helpers/imaginary/gallery-images';
import { venueImaginaryCategory } from '@/helpers/imaginary/venue-category';
import { flash } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useTreatwellStore } from '@/stores/treatwell';
import { GET_TREATWELL_VENUE, UPDATE_TREATWELL_VENUE } from '../graphql';

const { hasActiveVenue } = useTreatwellStore();
const { treatwellStatus } = storeToRefs(useTreatwellStore());
const { t } = useI18n();
const router = useRouter();

const { loading: loadingVenue, onResult } = useQuery(GET_TREATWELL_VENUE);
const images = ref<string[]>([]);
const galleryImages = useGalleryImages(
  () => images.value,
  (sources) => {
    images.value = sources;
  },
);

const maxPhotos = 15;
const minWidth = 1920;
const minHeight = 1080;

const imaginaryCategory = computed(() => venueImaginaryCategory());
type ImageRejection = 'orientation' | 'resolution';

const imageRejections = ref<ImageRejection[]>([]);
const saveFailed = ref(false);

const addImageRejection = (rejection: ImageRejection) => {
  if (!imageRejections.value.includes(rejection)) {
    imageRejections.value = [...imageRejections.value, rejection];
  }
};

const emit = defineEmits(['close', 'event']);

onResult(({ data: { treatwellVenue } }) => {
  images.value = treatwellVenue.images || [];
});

const close = () => {
  router.push({
    name: 'treatwell-admin',
  });
};

const {
  mutate: updateSalonPhotos,
  onDone,
  loading: isSaving,
} = useMutation(UPDATE_TREATWELL_VENUE);

const fileAmountWarning = ref('');

const submit = () => {
  if (images.value.length < 3 && !hasActiveVenue) {
    fileAmountWarning.value = t(
      'admin.treatwell.photos_modal.min_photos_required',
      { count: 3 },
    );
    return;
  }
  if (images.value.length < 5 && hasActiveVenue) {
    fileAmountWarning.value = t(
      'admin.treatwell.photos_modal.min_photos_required',
      { count: 5 },
    );
    return;
  }

  if (images.value.length > maxPhotos) {
    fileAmountWarning.value = t(
      'admin.treatwell.photos_modal.max_photos_required',
    );
    return;
  }

  saveFailed.value = false;
  treatwellStatus.value.hasPhotos = false;
  updateSalonPhotos({
    input: {
      images: images.value,
    },
  });
};

watch(images, () => {
  fileAmountWarning.value = '';
});

onDone(({ data }) => {
  if (data?.updateTreatwellVenue?.errors) {
    saveFailed.value = true;
    return;
  }

  flash(t('global.flash.treatwell_integration_updated'));
  treatwellStatus.value.hasPhotos = true;
  emit('event', 'provide_photos');
  close();
});

const { company } = useCompanyStore();
const photoGuidelinesUrl = computed(() => {
  switch (company.country) {
    case 'nl':
      return 'https://salonized.s3.eu-west-1.amazonaws.com/public/Marketplace%20Salon%20Fotografie%20Richtlijnen%20-%20NL.pdf';
    case 'de':
      return 'https://salonized.s3.eu-west-1.amazonaws.com/public/Marketplace%20Salon%20Fotografie%20Leitlinien%20-%20DE.pdf';
  }
  return 'https://salonized.s3.eu-west-1.amazonaws.com/public/Marketplace%20Salon%20Photography%20Guidelines%20-%20EN.pdf';
});
</script>
